import { LoadingOutlined } from '@ant-design/icons';
import { Button, Form, Grid, Input, Modal, Spin, Typography, message } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelectionHeader } from 'context/AppSelectionHeaderContext';
import { findFarmOptionById, setCurrentFarmContext, userHasFarmAccess } from 'farmContext';
import { useAccountInfo, useSetAccountFarm } from 'hooks/useAccount';
import { useFarmPickerCatalog } from 'hooks/useFarmPickerCatalog';
import { useGetAssignedFarmUsersQuery, useGetFarmInfo, useGetProdPlacesByFarmQuery, useSelfAddProdPlace } from 'hooks/useFarms';
import { storageKeys } from 'static';
import { setAnalyticsFarmContext } from 'analytics/ga4';
import { AccountInfoData, FarmProdPlaceData, GetAssignedFarmUsersData, GetFarmData, GetProdPlacesByFarmData, TFunction } from 'types';
import { useQueryClient } from 'react-query';
import { accountInfoQueryKey } from 'api/account';
import {
  countFarmProducts,
  hasBoardProductAccess,
  hasCropsProductAccess,
  hasLivestockProductAccess,
  hasStandaloneChemicalJournalAccess,
} from 'utils/farmProductAccess';
import { getFarmPickerContextMeta } from 'utils/farmPickerOptions';
import { findFarmInOrganisations } from 'utils/organisationContext';

const { useBreakpoint } = Grid;

const getRecentFarmId = () => sessionStorage.getItem(storageKeys.RecentFarmId);

const getStringValue = (value: string | number | null | undefined) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const getFarmIdFromAccountInfo = (accountInfo?: AccountInfoData) => {
  const candidates = [
    accountInfo?.selectedFarmId,
    accountInfo?.userFarmRoles?.[0]?.farmId,
    accountInfo?.userFarms?.[0]?.id,
    accountInfo?.userFarms?.[0]?.farmId,
  ];

  const farmId = candidates.find((candidate) => getStringValue(candidate).length > 0);
  return farmId ? String(farmId) : null;
};

const formatValue = (value: string | number | null | undefined, fallback: string) => {
  const formattedValue = getStringValue(value);
  return formattedValue.length ? formattedValue : fallback;
};

const formatBoolean = (value: boolean | undefined | null, t: TFunction['t'], fallback: string) => {
  if (value === undefined || value === null) {
    return fallback;
  }

  return value ? t('Yes') : t('No');
};

const formatCompanyType = (value: string | null | undefined, t: TFunction['t'], fallback: string) => {
  const normalizedValue = getStringValue(value).toUpperCase();

  if (!normalizedValue) {
    return fallback;
  }

  if (normalizedValue === 'P') {
    return t('CompanyTypePersonal');
  }

  if (normalizedValue === 'O') {
    return t('CompanyTypeOrganization');
  }

  return normalizedValue;
};

const getProdPlaceLabels = (prodPlaces: FarmProdPlaceData[] | undefined, fallback: string) => {
  if (!Array.isArray(prodPlaces) || !prodPlaces.length) {
    return [fallback];
  }

  return prodPlaces.map((prodPlace) => {
    const code = prodPlace?.code ? String(prodPlace.code).trim() : '';
    const displayName = prodPlace?.displayName ? String(prodPlace.displayName).trim() : '';
    const name = prodPlace?.name ? String(prodPlace.name).trim() : '';
    const label = displayName || name;

    if (code && label && code !== label) {
      return `${code} - ${label}`;
    }

    return code || label || fallback;
  });
};

const countAssignedFarmUsers = (data?: GetAssignedFarmUsersData | null) => {
  if (Array.isArray(data)) {
    return data.length;
  }

  const users = data?.users;
  if (Array.isArray(users)) {
    return users.length;
  }

  return 0;
};

const extractProdPlaces = (
  data: GetProdPlacesByFarmData | undefined,
  fallbackProdPlaces?: FarmProdPlaceData[],
) => {
  if (Array.isArray(data)) {
    return data;
  }

  const candidates = [
    data?.prodPlaceData,
    data?.prodPlaces,
    data?.productionPlaces,
    data?.items,
    fallbackProdPlaces,
  ];

  return candidates.find((items): items is FarmProdPlaceData[] => Array.isArray(items)) || [];
};

const cardBorder = 'none';
const cardShadow = '2px 4px 4px 0px rgba(204, 204, 204, 0.25)';
const cardRadius = 8;
const pageContentMaxWidth = 1440;
const LIVESTOCK_SPECIES_ID = 1;
const neutralActionButtonStyle = {
  height: 40,
  borderRadius: 6,
  paddingInline: 14,
  backgroundColor: '#4a4d4e',
  borderColor: '#4a4d4e',
  color: '#ffffff',
  fontSize: 14,
  fontWeight: 600,
  boxShadow: '0 2px 0 rgba(0, 0, 0, 0.043)',
};
const modalSecondaryButtonStyle = {
  height: 40,
  borderRadius: 6,
  paddingInline: 14,
  fontSize: 14,
  fontWeight: 600,
};
const modalInputStyle = {
  borderRadius: 6,
};

const normalizeProdPlaceCode = (value: string) => {
  const trimmedValue = value.trim().toUpperCase();
  const digits = trimmedValue.replace(/\D/g, '');

  if (digits.length) {
    return `SE${digits.slice(0, 6).padStart(6, '0')}`;
  }

  if (trimmedValue.startsWith('SE')) {
    return trimmedValue.slice(0, 8);
  }

  return trimmedValue;
};

type DetailRow = {
  label: string;
  value?: string;
  badge?: {
    isActive: boolean;
    label: string;
  };
};

function SectionCard({
  title,
  subtitle,
  extra,
  children,
}: {
  title: string;
  subtitle?: string;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        background: '#fff',
        borderRadius: cardRadius,
        border: cardBorder,
        boxShadow: cardShadow,
        padding: 22,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Typography.Title level={4} style={{ margin: 0, fontSize: 18, color: '#101828' }}>
            {title}
          </Typography.Title>
          {subtitle ? (
            <Typography.Text
              style={{
                display: 'block',
                marginTop: 4,
                color: '#667085',
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </Typography.Text>
          ) : null}
        </div>
        {extra}
      </div>
      {children}
    </section>
  );
}

function StatusBadge({
  isActive,
  label,
}: {
  isActive: boolean;
  label: string;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 999,
        border: isActive ? '1px solid rgba(18, 183, 106, 0.22)' : '1px solid rgba(71, 84, 103, 0.16)',
        background: isActive ? '#ecfdf3' : '#f8fafc',
        color: isActive ? '#027a48' : '#475467',
        fontSize: 13,
        fontWeight: 700,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: isActive ? '#12b76a' : '#98a2b3',
        }}
      />
      {label}
    </span>
  );
}

function SummaryStat({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div
      style={{
        padding: '16px 18px',
        borderRadius: cardRadius,
        background: 'rgba(255, 255, 255, 0.86)',
        border: 'none',
        boxShadow: cardShadow,
        backdropFilter: 'blur(8px)',
      }}
    >
      <Typography.Text
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.9,
          color: '#98a2b3',
          marginBottom: 8,
        }}
      >
        {label}
      </Typography.Text>
      <Typography.Text
        style={{
          display: 'block',
          fontSize: 16,
          lineHeight: 1.4,
          color: '#101828',
          fontWeight: 700,
        }}
      >
        {value}
      </Typography.Text>
    </div>
  );
}

function DetailSection({
  title,
  subtitle,
  footer,
  fallback,
  isCompact,
  rows,
}: {
  title: string;
  subtitle?: string;
  footer?: ReactNode;
  fallback: string;
  isCompact: boolean;
  rows: DetailRow[];
}) {
  return (
    <SectionCard title={title} subtitle={subtitle}>
      {rows.map((row, index) => (
        <div
          key={`${title}-${row.label}`}
          style={{
            display: 'grid',
            gridTemplateColumns: isCompact ? '1fr' : 'minmax(180px, 0.8fr) minmax(0, 1fr)',
            gap: isCompact ? 4 : 16,
            alignItems: 'start',
            padding: '14px 0',
            borderBottom: index === rows.length - 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
          }}
        >
          <Typography.Text
            style={{
              color: '#98a2b3',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
            }}
          >
            {row.label}
          </Typography.Text>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isCompact ? 'flex-start' : 'flex-end',
              gap: 6,
            }}
          >
            {row.badge ? (
              <StatusBadge isActive={row.badge.isActive} label={row.badge.label} />
            ) : null}
            {row.value !== undefined ? (
              <Typography.Text
                style={{
                  color: row.value === fallback ? '#98a2b3' : '#101828',
                  fontSize: row.badge ? 13 : 15,
                  fontWeight: row.value === fallback ? 500 : 700,
                  textAlign: isCompact ? 'left' : 'right',
                  lineHeight: 1.5,
                }}
              >
                {row.value}
              </Typography.Text>
            ) : null}
          </div>
        </div>
      ))}
      {footer ? <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>{footer}</div> : null}
    </SectionCard>
  );
}

function ListSection({
  title,
  subtitle,
  fallback,
  isCompact,
  items,
  action,
}: {
  title: string;
  subtitle?: string;
  fallback: string;
  isCompact: boolean;
  items: string[];
  action?: ReactNode;
}) {
  const hasItems = !(items.length === 1 && items[0] === fallback);

  return (
    <SectionCard
      title={title}
      subtitle={subtitle}
      extra={(
        <span
          style={{
            minWidth: 32,
            height: 32,
            padding: '0 10px',
            borderRadius: 999,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            background: '#f8fafc',
            color: '#475467',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {hasItems ? items.length : 0}
        </span>
      )}
    >
      {action ? (
        <div
          style={{
            display: 'flex',
            justifyContent: isCompact ? 'stretch' : 'flex-start',
            marginBottom: 12,
          }}
        >
          <div style={{ width: isCompact ? '100%' : 'auto' }}>{action}</div>
        </div>
      ) : null}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {items.map((item, index) => (
          <Typography.Text
            key={`${title}-${item}-${index}`}
            style={{
              display: 'block',
              color: item === fallback ? '#98a2b3' : '#101828',
              fontSize: 15,
              fontWeight: item === fallback ? 500 : 700,
              lineHeight: 1.5,
              wordBreak: 'break-word',
            }}
          >
            {item}
          </Typography.Text>
        ))}
      </div>
    </SectionCard>
  );
}

export default function FarmDetails() {
  const { t }: TFunction = useTranslation();
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [farmInfo, setFarmInfo] = useState<GetFarmData | null>(null);
  const [isProdPlaceModalOpen, setIsProdPlaceModalOpen] = useState(false);
  const [prodPlaceForm] = Form.useForm();
  const farmIdFromQuery = searchParams.get('id');
  const recentFarmId = getRecentFarmId();
  const queryClient = useQueryClient();
  const { data: accountInfo, isLoading: isLoadingAccountInfo, isError: failedFetchingAccountInfo, refetch: refetchAccountInfo } = useAccountInfo();
  const { mutateAsync: setAccountFarm, isLoading: isSettingAccountFarm } = useSetAccountFarm();
  const setFarmAttemptedRef = useRef('');
  const { setFarmPicker } = useAppSelectionHeader();
  const {
    farmOptions,
    pickerOptions,
    organisations,
    isLoadingOrganisations,
  } = useFarmPickerCatalog(accountInfo);
  const farmIdFromAccount = getFarmIdFromAccountInfo(accountInfo);
  const farmId = farmIdFromQuery || recentFarmId || farmIdFromAccount || null;
  const selectedFarmOption = useMemo(() => {
    const matched =
      findFarmOptionById(farmOptions, farmId) ||
      findFarmOptionById(farmOptions, accountInfo?.selectedFarmId);

    if (matched) {
      return matched;
    }

    const fallbackId = getStringValue(farmId);
    if (!fallbackId) {
      return undefined;
    }

    return {
      farmId: fallbackId,
      farmIdCandidates: [fallbackId],
      farmName: getStringValue(accountInfo?.selectedFarmName) || fallbackId,
      label: getStringValue(accountInfo?.selectedFarmName) || fallbackId,
      value: fallbackId,
    };
  }, [accountInfo?.selectedFarmId, accountInfo?.selectedFarmName, farmId, farmOptions]);
  const { mutate: getFarmInfo, isLoading: isLoadingFarmInfo, isError: failedFetchingFarmInfo } = useGetFarmInfo();
  const { mutateAsync: selfAddProdPlace, isLoading: isSelfAddingProdPlace } = useSelfAddProdPlace();
  const {
    data: prodPlacesData,
    isLoading: isLoadingProdPlaces,
    refetch: refetchProdPlaces,
  } = useGetProdPlacesByFarmQuery(farmId);
  const { data: assignedFarmUsersData } = useGetAssignedFarmUsersQuery(farmId);

  const loadFarmInfo = (selectedFarmId: string, shouldResetState = false) => {
    if (shouldResetState) {
      setFarmInfo(null);
    }

    getFarmInfo(
      { id: selectedFarmId },
      {
        onSuccess: (data: GetFarmData) => {
          setFarmInfo(data);
          const farmName = data?.farmName?.trim();
          const orgNumber = getStringValue(data?.companyRegistration);

          if (farmName) {
            sessionStorage.setItem(storageKeys.RecentFarmName, farmName);
          }
          if (orgNumber) {
            sessionStorage.setItem(storageKeys.RecentFarmOrgNumber, orgNumber);
          } else {
            sessionStorage.removeItem(storageKeys.RecentFarmOrgNumber);
          }

          setAnalyticsFarmContext(selectedFarmId, farmName || undefined, orgNumber || undefined);
        },
      }
    );
  };

  useEffect(() => {
    if (!farmId) {
      return;
    }

    sessionStorage.setItem(storageKeys.RecentFarmId, farmId);
    setAnalyticsFarmContext(farmId);
    void queryClient.invalidateQueries(accountInfoQueryKey());
    loadFarmInfo(farmId, true);
  }, [farmId, queryClient]);

  useEffect(() => {
    if (!farmId || isLoadingAccountInfo || isSettingAccountFarm) {
      return;
    }

    const selectedId = getStringValue(accountInfo?.selectedFarmId);
    if (selectedId === farmId) {
      setFarmAttemptedRef.current = '';
      return;
    }

    if (setFarmAttemptedRef.current === farmId) {
      return;
    }

    const canSelect =
      Boolean(findFarmOptionById(farmOptions, farmId)) ||
      userHasFarmAccess(accountInfo, farmId) ||
      findFarmInOrganisations(organisations, farmId);

    if (!canSelect) {
      return;
    }

    setFarmAttemptedRef.current = farmId;
    void setAccountFarm(farmId)
      .then(() => refetchAccountInfo())
      .catch(() => {
        message.error(t('AnErrorOccurred'));
      });
  }, [
    accountInfo?.selectedFarmId,
    farmId,
    farmOptions,
    isLoadingAccountInfo,
    isSettingAccountFarm,
    organisations,
    refetchAccountInfo,
    setAccountFarm,
    t,
  ]);

  const handleHeaderFarmChange = useCallback(async (nextFarmValue: string | number) => {
    const nextFarmOption = findFarmOptionById(farmOptions, nextFarmValue);

    if (!nextFarmOption || !selectedFarmOption || nextFarmOption.farmId === selectedFarmOption.farmId) {
      return;
    }

    const previousFarmContext = {
      farmId: selectedFarmOption.farmId,
      farmName: selectedFarmOption.farmName,
      orgNumber: selectedFarmOption.orgNumber,
    };

    setCurrentFarmContext({
      farmId: nextFarmOption.farmId,
      farmName: nextFarmOption.farmName,
      orgNumber: nextFarmOption.orgNumber,
    });

    navigate(`/farm-details?id=${encodeURIComponent(nextFarmOption.farmId)}`, { replace: true });

    try {
      await setAccountFarm(nextFarmOption.farmId);
      void refetchAccountInfo();
    } catch {
      setCurrentFarmContext(previousFarmContext);
      navigate(
        `/farm-details?id=${encodeURIComponent(previousFarmContext.farmId)}`,
        { replace: true },
      );
      message.error(t('AnErrorOccurred'));
    }
  }, [
    farmOptions,
    navigate,
    refetchAccountInfo,
    selectedFarmOption,
    setAccountFarm,
    t,
  ]);

  useEffect(() => {
    if (!pickerOptions.length && !isLoadingAccountInfo) {
      setFarmPicker(null);
      return () => setFarmPicker(null);
    }

    const { hierarchy, farmRoleName } = getFarmPickerContextMeta(
      organisations,
      accountInfo,
      selectedFarmOption?.farmId || farmId,
    );

    const headerPickerOptions =
      selectedFarmOption &&
      !pickerOptions.some((option) => String(option.value) === String(selectedFarmOption.farmId))
        ? [
            ...pickerOptions,
            {
              value: selectedFarmOption.farmId,
              label: selectedFarmOption.label,
            },
          ]
        : pickerOptions;

    setFarmPicker({
      disabled: isSettingAccountFarm,
      farmRoleName,
      hierarchy,
      label: t('ActiveFarm'),
      loading: isLoadingAccountInfo || isSettingAccountFarm || isLoadingOrganisations,
      onChange: (nextFarmValue) => {
        void handleHeaderFarmChange(nextFarmValue);
      },
      options: headerPickerOptions,
      placeholder: t('CropsFarmPickerPlaceholder'),
      value: selectedFarmOption?.farmId ?? farmId ?? undefined,
    });

    return () => setFarmPicker(null);
  }, [
    accountInfo,
    farmId,
    handleHeaderFarmChange,
    isLoadingAccountInfo,
    isLoadingOrganisations,
    isSettingAccountFarm,
    organisations,
    pickerOptions,
    selectedFarmOption?.farmId,
    selectedFarmOption?.value,
    setFarmPicker,
    t,
  ]);

  const isCompact = !screens.md;
  const useSplitLayout = !!screens.lg;

  if (isLoadingAccountInfo || (!!farmId && ((!farmInfo && isLoadingFarmInfo) || isLoadingProdPlaces))) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 72, paddingBottom: 48 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
      </div>
    );
  }

  if (!farmId) {
    return (
      <div style={{ paddingTop: 16, paddingBottom: 40 }}>
        <div
          style={{
            maxWidth: 720,
            margin: '0 auto',
            background: '#fff',
            borderRadius: cardRadius,
            border: cardBorder,
            boxShadow: cardShadow,
            padding: isCompact ? '28px 20px' : '36px 32px',
            textAlign: 'center',
          }}
        >
          <Typography.Title
            level={2}
            style={{
              marginTop: 0,
              marginBottom: 10,
              color: '#101828',
            }}
          >
            {t('FarmDetailsAddFarmTitle')}
          </Typography.Title>
          <Typography.Paragraph
            style={{
              color: '#475467',
              marginBottom: 24,
              maxWidth: 520,
              marginInline: 'auto',
            }}
          >
            {t('RegisterFarmPromoText')}
          </Typography.Paragraph>
          <Button
            type="primary"
            size="large"
            style={{
              width: isCompact ? '100%' : 'auto',
              minWidth: 180,
              height: 48,
              borderRadius: 12,
              backgroundColor: '#ff6b3d',
              borderColor: '#ff6b3d',
            }}
            onClick={() => navigate('/register-farm', { replace: true })}
          >
            {t('RegisterFarmButton')}
          </Button>
        </div>
      </div>
    );
  }

  if (!farmInfo) {
    return (
      <div style={{ paddingTop: 16, paddingBottom: 40 }}>
        <div
          style={{
            maxWidth: 720,
            margin: '0 auto',
            background: '#fff',
            borderRadius: cardRadius,
            border: cardBorder,
            boxShadow: cardShadow,
            padding: isCompact ? '28px 20px' : '36px 32px',
            textAlign: 'center',
          }}
        >
          <Typography.Title level={1} style={{ marginBottom: 12 }}>
            {t('AnErrorOccurred')}
          </Typography.Title>
          <Typography.Paragraph style={{ color: '#6b7280', marginBottom: 28, maxWidth: 460, marginInline: 'auto' }}>
            {failedFetchingAccountInfo || failedFetchingFarmInfo ? t('AnErrorOccurred') : t('NotAvailable')}
          </Typography.Paragraph>
          <Button
            type="primary"
            size="large"
            style={{
              width: isCompact ? '100%' : 'auto',
              minWidth: 180,
              height: 48,
              borderRadius: 12,
              backgroundColor: '#ff6b3d',
              borderColor: '#ff6b3d',
            }}
            onClick={() => navigate('/register-farm', { replace: true })}
          >
            {t('Back')}
          </Button>
        </div>
      </div>
    );
  }

  const fallback = t('NotAvailable');
  const prodPlaces = extractProdPlaces(prodPlacesData, farmInfo.prodPlaceData);
  const prodPlaceLabels = getProdPlaceLabels(prodPlaces, t('NoProductionPlacesYet'));
  const moduleCount = countFarmProducts(accountInfo);
  const cropsModuleActive = hasCropsProductAccess(accountInfo);
  const chemicalJournalActive = hasStandaloneChemicalJournalAccess(accountInfo);
  const cropsModuleLabel = cropsModuleActive ? t('CropsAndChemicalJournal') : t('Crops');
  // User portal get-farm-info omits `users` for non-admin callers; use same source as Användare page.
  const userCount =
    assignedFarmUsersData !== undefined
      ? countAssignedFarmUsers(assignedFarmUsersData)
      : Array.isArray(farmInfo.users)
        ? farmInfo.users.length
        : 0;
  const statusLabel = farmInfo.isActive ? t('Active') : t('Inactive');
  const inactiveModuleLabel = t('NotActive');
  const heroTitle = getStringValue(farmInfo.farmName) || t('FarmDetailsTitle');

  const formatBillingLine = (companyName?: string, companyRegistration?: string) => {
    const parts = [getStringValue(companyName), getStringValue(companyRegistration)].filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
  };

  const billingLineForModuleKeys = (moduleKeys: string[]) => {
    const moduleRows = accountInfo?.allowedModules ?? [];
    for (const moduleRow of moduleRows) {
      const moduleKey = getStringValue(moduleRow?.name).toLowerCase();
      if (!moduleKeys.includes(moduleKey)) {
        continue;
      }
      const line = formatBillingLine(
        moduleRow?.billingCompanyName,
        moduleRow?.billingCompanyRegistration,
      );
      if (line) {
        return line;
      }
    }
    return undefined;
  };

  const boardBilling = billingLineForModuleKeys(['tavla']);
  const cropsBilling = billingLineForModuleKeys(['vaxt']);
  const chemicalBilling = billingLineForModuleKeys(['kemi']);
  const livestockBilling = billingLineForModuleKeys(['diko', 'slut']);

  // Billing is per module — never collapse to a single farm-level company.
  const moduleBillingRows: DetailRow[] = [
    ...(hasBoardProductAccess(accountInfo) && boardBilling
      ? [{ label: t('BulletinBoard'), value: boardBilling }]
      : []),
    ...(cropsModuleActive && cropsBilling
      ? [{ label: cropsModuleLabel, value: cropsBilling }]
      : []),
    ...(!cropsModuleActive && chemicalJournalActive && chemicalBilling
      ? [{ label: t('ChemicalJournal'), value: chemicalBilling }]
      : []),
    ...(hasLivestockProductAccess(accountInfo) && livestockBilling
      ? [{ label: t('Livestock'), value: livestockBilling }]
      : []),
  ];

  const heroMeta = [
    moduleBillingRows.length === 1 ? moduleBillingRows[0].value : undefined,
    getStringValue(farmInfo.city),
  ].filter(Boolean).join(' • ');

  const openAddProdPlaceModal = () => {
    prodPlaceForm.resetFields();
    setIsProdPlaceModalOpen(true);
  };

  const closeAddProdPlaceModal = () => {
    setIsProdPlaceModalOpen(false);
    prodPlaceForm.resetFields();
  };

  const saveProductionPlace = async () => {
    if (!farmId) {
      return;
    }

    const values = await prodPlaceForm.validateFields();
    const normalizedProdPlaceCode = normalizeProdPlaceCode(getStringValue(values?.code));

    if (!/^SE\d{6}$/.test(normalizedProdPlaceCode)) {
      prodPlaceForm.setFields([{ name: 'code', errors: [t('NeedToBeSE123456')] }]);
      return;
    }

    if (normalizedProdPlaceCode === 'SE000000') {
      prodPlaceForm.setFields([{ name: 'code', errors: [t('CanNotBeSE000000')] }]);
      return;
    }

    await selfAddProdPlace({
      code: normalizedProdPlaceCode,
      farmId,
      name: normalizedProdPlaceCode,
      speciesId: String(LIVESTOCK_SPECIES_ID),
    });

    await refetchProdPlaces();
    message.success(t('AddProductionPlaceSuccess'));
    closeAddProdPlaceModal();
    loadFarmInfo(farmId, false);
  };

  return (
    <div style={{ paddingTop: 8, paddingBottom: 40 }}>
      <div style={{ maxWidth: pageContentMaxWidth, margin: '0 auto' }}>
        <section
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 58%, #fff5ef 100%)',
            borderRadius: cardRadius,
            border: cardBorder,
            boxShadow: cardShadow,
            padding: isCompact ? '26px 20px' : '34px 32px',
            marginBottom: 24,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: -100,
              right: -80,
              width: 240,
              height: 240,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255, 107, 61, 0.18) 0%, rgba(255, 107, 61, 0) 72%)',
            }}
          />
          <div
            style={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: useSplitLayout ? 'minmax(0, 1.2fr) minmax(340px, 0.8fr)' : '1fr',
              gap: 24,
              alignItems: 'start',
            }}
          >
            <div>
              <Typography.Text
                style={{
                  display: 'inline-block',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1.1,
                  color: '#98a2b3',
                  marginBottom: 12,
                }}
              >
                {t('FarmDetailsTitle')}
              </Typography.Text>
              <Typography.Title
                level={1}
                style={{
                  marginTop: 0,
                  marginBottom: 10,
                  fontSize: isCompact ? 30 : 38,
                  lineHeight: 1.08,
                  color: '#101828',
                }}
              >
                {heroTitle}
              </Typography.Title>
              {heroMeta ? (
                <Typography.Text
                  style={{
                    display: 'block',
                    fontSize: 15,
                    color: '#475467',
                    marginBottom: 12,
                  }}
                >
                  {heroMeta}
                </Typography.Text>
              ) : null}
              <div style={{ marginTop: 12 }}>
                <StatusBadge isActive={farmInfo.isActive} label={statusLabel} />
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isCompact ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                gap: 12,
              }}
            >
              <SummaryStat label={t('FarmDetailsProductCount')} value={String(moduleCount)} />
              <SummaryStat label={t('FarmDetailsUserCount')} value={String(userCount)} />
            </div>
          </div>
        </section>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: useSplitLayout ? 'minmax(0, 1.08fr) minmax(320px, 0.92fr)' : '1fr',
            gap: 24,
          }}
        >
          <div style={{ display: 'grid', gap: 16 }}>
            <DetailSection
              title={t('FarmSummaryCompanySection')}
              subtitle={t('FarmDetailsBillingCompaniesHint')}
              fallback={fallback}
              isCompact={isCompact}
              rows={
                moduleBillingRows.length
                  ? moduleBillingRows
                  : [{ label: t('ContextCompany'), value: fallback }]
              }
            />

            <DetailSection
              title={t('FarmSummaryContactSection')}
              fallback={fallback}
              isCompact={isCompact}
              rows={[
                { label: t('FirstName'), value: formatValue(farmInfo.firstName, fallback) },
                { label: t('LastName'), value: formatValue(farmInfo.lastName, fallback) },
                { label: t('Email'), value: formatValue(farmInfo.email, fallback) },
                { label: t('Phone'), value: formatValue(farmInfo.phone, fallback) },
                { label: t('MobilePhone'), value: formatValue(farmInfo.mobilePhone, fallback) },
              ]}
            />

            <DetailSection
              title={t('FarmSummaryAddressSection')}
              fallback={fallback}
              isCompact={isCompact}
              rows={[
                { label: t('Address'), value: formatValue(farmInfo.address1, fallback) },
                { label: t('Address2'), value: formatValue(farmInfo.address2, fallback) },
                { label: t('ZipCode'), value: formatValue(farmInfo.zipCode, fallback) },
                { label: t('City'), value: formatValue(farmInfo.city, fallback) },
                { label: t('District'), value: formatValue(farmInfo.district, fallback) },
              ]}
            />
          </div>

          <div style={{ display: 'grid', gap: 16 }}>
            <DetailSection
              title={t('FarmDetailsAccessSection')}
              subtitle={t('FarmDetailsModulesHint')}
              fallback={fallback}
              isCompact={isCompact}
              footer={(
                <Link to="/companies" style={{ fontWeight: 600, color: '#ff6b3d' }}>
                  {t('FarmDetailsManageCompaniesLink')} →
                </Link>
              )}
              rows={[
                {
                  label: t('BulletinBoard'),
                  value: hasBoardProductAccess(accountInfo)
                    ? (boardBilling ? `${t('ContextCompany')}: ${boardBilling}` : undefined)
                    : undefined,
                  badge: {
                    isActive: hasBoardProductAccess(accountInfo),
                    label: hasBoardProductAccess(accountInfo) ? t('Active') : inactiveModuleLabel,
                  },
                },
                {
                  label: cropsModuleLabel,
                  value: cropsModuleActive
                    ? (cropsBilling ? `${t('ContextCompany')}: ${cropsBilling}` : undefined)
                    : undefined,
                  badge: {
                    isActive: cropsModuleActive,
                    label: cropsModuleActive ? t('Active') : inactiveModuleLabel,
                  },
                },
                ...(!cropsModuleActive
                  ? [{
                    label: t('ChemicalJournal'),
                    value: chemicalJournalActive
                      ? (chemicalBilling ? `${t('ContextCompany')}: ${chemicalBilling}` : undefined)
                      : undefined,
                    badge: {
                      isActive: chemicalJournalActive,
                      label: chemicalJournalActive ? t('Active') : inactiveModuleLabel,
                    },
                  }]
                  : []),
                {
                  label: t('Livestock'),
                  value: hasLivestockProductAccess(accountInfo)
                    ? (livestockBilling ? `${t('ContextCompany')}: ${livestockBilling}` : undefined)
                    : undefined,
                  badge: {
                    isActive: hasLivestockProductAccess(accountInfo),
                    label: hasLivestockProductAccess(accountInfo) ? t('Active') : inactiveModuleLabel,
                  },
                },
              ]}
            />

            <ListSection
              title={t('ProductionPlaces')}
              subtitle={t('ProductionPlacesSubtitle')}
              fallback={t('NoProductionPlacesYet')}
              isCompact={isCompact}
              items={prodPlaceLabels}
              action={(
                <Button
                  type="primary"
                  size="large"
                  onClick={openAddProdPlaceModal}
                  style={neutralActionButtonStyle}
                >
                  {t('AddProductionPlace')}
                </Button>
              )}
            />
          </div>
        </div>
      </div>

      <Modal
        title={t('AddProductionPlace')}
        open={isProdPlaceModalOpen}
        onCancel={() => {
          if (isSelfAddingProdPlace) {
            return;
          }

          closeAddProdPlaceModal();
        }}
        maskClosable={!isSelfAddingProdPlace}
        closable={!isSelfAddingProdPlace}
        footer={[
          <Button
            key="back"
            size="large"
            disabled={isSelfAddingProdPlace}
            style={modalSecondaryButtonStyle}
            onClick={closeAddProdPlaceModal}
          >
            {t('Back')}
          </Button>,
          <Button
            key="save"
            type="primary"
            size="large"
            loading={isSelfAddingProdPlace}
            style={neutralActionButtonStyle}
            onClick={() => {
              void saveProductionPlace();
            }}
          >
            {t('Save')}
          </Button>,
        ]}
      >
        <Form
          form={prodPlaceForm}
          layout="vertical"
          onFinish={() => {
            void saveProductionPlace();
          }}
        >
          <Form.Item
            label={t('PPCode')}
            name="code"
            rules={[
              { required: true, message: t('PPCodeIsRequired') },
            ]}
          >
            <Input
              size="large"
              style={modalInputStyle}
              placeholder={t('PPCodeExample')}
              maxLength={8}
              onBlur={(event) => {
                const formattedValue = normalizeProdPlaceCode(event.target.value || '');
                prodPlaceForm.setFieldsValue({ code: formattedValue });
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
